import React, { useState, useEffect } from 'react';
import NexusSelection from '../components/Showdown/NexusSelection';
import ShowdownLobby from '../components/Showdown/ShowdownLobby';
import ShowdownArena from '../components/Showdown/ShowdownArena';
import ChallengePage from './ChallengePage';
import ShowdownChat from '../components/Showdown/ShowdownChat';
import TournamentCreationPage from './TournamentCreationPage';
import { gameComponents } from '../gameComponents';
import SideBySideViewer from '../components/Showdown/SideBySideViewer';
import SpectatorPage from './SpectatorPage';
import TournamentBracket from '../components/Showdown/TournamentBracket';
import LeaderboardPage from './LeaderboardPage';
import QuestBoard from './QuestBoard';
import RewardsPage from './RewardsPage';
import ReadyUpPage from './ReadyUpPage';
import MatchHistoryPage from './MatchHistoryPage';
import { MessageCircle, X, Swords, Trophy, Target, Award, BarChart2 } from 'lucide-react';
import { useAppContext } from '../AppContext';
import { doc, getDoc } from 'firebase/firestore';
import LoadingSpinner from '../components/LoadingSpinner';
import { motion, AnimatePresence } from 'framer-motion';

// Modal Wrapper Component
const ShowdownModalWrapper = ({ children, onClose, title }) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 md:p-8">
        <div className="bg-gray-900/95 border border-white/10 w-full h-full max-w-7xl rounded-2xl shadow-2xl overflow-hidden flex flex-col relative">
            <div className="p-4 border-b border-white/10 flex justify-between items-center bg-black/40">
                <h2 className="text-xl font-bold text-white font-orbitron">{title}</h2>
                <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full transition-colors">
                    <X size={24} className="text-gray-400 hover:text-white" />
                </button>
            </div>
            <div className="flex-grow overflow-hidden relative">
                {children}
            </div>
        </div>
    </div>
);

const ShowdownPage = () => {
    const { db, user, currentUserProfile } = useAppContext();
    const [primaryNexus, setPrimaryNexus] = useState(null);
    const [nexusDetails, setNexusDetails] = useState(null);
    const [activeModal, setActiveModal] = useState(null); // Replaces 'view' for navigation
    const [activeChallengeId, setActiveChallengeId] = useState(null);
    const [selectedMatch, setSelectedMatch] = useState(null);
    const [selectedTournament, setSelectedTournament] = useState(null);
    const [isChatOpen, setIsChatOpen] = useState(true); // Chat open by default on desktop

    useEffect(() => {
        if (user?.primaryShowdownNexus) {
            setPrimaryNexus(user.primaryShowdownNexus);
        }
    }, [user]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const challengeId = params.get('challengeId') || params.get('activeChallengeId');
        if (challengeId) {
            startChallenge(challengeId);
        }
    }, []);

    useEffect(() => {
        if (primaryNexus && db) {
            const fetchNexusDetails = async () => {
                const nexusRef = doc(db, `artifacts/whispers-of-harmony/public/data/nexuses`, primaryNexus);
                const nexusDoc = await getDoc(nexusRef);
                if (nexusDoc.exists()) {
                    setNexusDetails(nexusDoc.data());
                }
            };
            fetchNexusDetails();
        }
    }, [primaryNexus, db]);

    const handleNexusSelection = (nexusId) => {
        setPrimaryNexus(nexusId);
    };

    const startChallenge = (challengeId) => {
        setActiveChallengeId(challengeId);
        setActiveModal('arena');
    };

    // Hub Stats
    const stats = [
        { label: 'Souls', value: currentUserProfile?.souls || 0, icon: Target, color: 'text-cyan-400' },
        { label: 'Wins', value: currentUserProfile?.wins || 0, icon: Trophy, color: 'text-yellow-400' },
        { label: 'Rank', value: currentUserProfile?.rank || 'Unranked', icon: BarChart2, color: 'text-purple-400' },
    ];

    // Render Active Modal
    const renderModal = () => {
        if (!activeModal) return null;

        switch (activeModal) {
            case 'lobby':
                return <ShowdownModalWrapper title="Challenge Board" onClose={() => setActiveModal(null)}><ShowdownLobby nexus={nexusDetails} /></ShowdownModalWrapper>;
            case 'nexusSelection':
                return <ShowdownModalWrapper title="Select Nexus" onClose={() => setActiveModal(null)}><NexusSelection onNexusSelected={handleNexusSelection} /></ShowdownModalWrapper>;
            case 'challenge':
                return <ShowdownModalWrapper title="Challenge a Rival" onClose={() => setActiveModal(null)}><ChallengePage startChallenge={startChallenge} /></ShowdownModalWrapper>;
            case 'tournaments':
                return <ShowdownModalWrapper title="Create Tournament" onClose={() => setActiveModal(null)}><TournamentCreationPage setView={setActiveModal} primaryNexus={primaryNexus} /></ShowdownModalWrapper>;
            case 'spectator':
                return <ShowdownModalWrapper title="Spectate Live" onClose={() => setActiveModal(null)}><SpectatorPage setView={setActiveModal} setSelectedMatch={setSelectedMatch} /></ShowdownModalWrapper>;
            case 'leaderboard':
                return <ShowdownModalWrapper title="Leaderboard" onClose={() => setActiveModal(null)}><LeaderboardPage /></ShowdownModalWrapper>;
            case 'quests':
                return <ShowdownModalWrapper title="Daily Quests" onClose={() => setActiveModal(null)}><QuestBoard /></ShowdownModalWrapper>;
            case 'rewards':
                return <ShowdownModalWrapper title="Titles & Rewards" onClose={() => setActiveModal(null)}><RewardsPage /></ShowdownModalWrapper>;
            case 'matchHistory':
                return <ShowdownModalWrapper title="Match History" onClose={() => setActiveModal(null)}><MatchHistoryPage /></ShowdownModalWrapper>;
            case 'readyUp': // Legacy fallback
                return <ShowdownModalWrapper title="Prepare for Battle" onClose={() => setActiveModal(null)}><ReadyUpPage challengeId={activeChallengeId} setView={setActiveModal} setSelectedMatch={setSelectedMatch} /></ShowdownModalWrapper>;
            case 'arena':
                // Render full screen without wrapper for immersion
                return (
                    <div className="fixed inset-0 z-50 bg-black">
                        <ShowdownArena challengeId={activeChallengeId} onExit={() => setActiveModal(null)} />
                    </div>
                );
            case 'spectateMatch':
                const GameComponent = gameComponents[selectedMatch.gameId];
                return (
                    <ShowdownModalWrapper title="Spectating Match" onClose={() => setActiveModal(null)}>
                        <SideBySideViewer 
                            gameComponent={GameComponent} 
                            challenger={{name: selectedMatch.challengerName, souls: selectedMatch.challengerSouls, gameProps: {dreamWeaveId: selectedMatch.challengerDreamWeaveId, nexusId: selectedMatch.challengerNexusId}}} 
                            challenged={{name: selectedMatch.challengedName, souls: selectedMatch.challengedSouls, gameProps: {dreamWeaveId: selectedMatch.challengedDreamWeaveId, nexusId: selectedMatch.challengedNexusId}}}
                        />
                    </ShowdownModalWrapper>
                );
            case 'viewTournament':
                return <ShowdownModalWrapper title="Tournament Bracket" onClose={() => setActiveModal(null)}><TournamentBracket tournamentId={selectedTournament} /></ShowdownModalWrapper>;
            default: return null;
        }
    };

    if (!primaryNexus) {
        return <NexusSelection onNexusSelected={handleNexusSelection} />;
    }

    return (
        <div className="w-full h-full flex relative bg-transparent overflow-hidden">
            {/* Main Hub Area (Left/Center) */}
            <div className={`flex-grow flex flex-col h-full transition-all duration-300 ${isChatOpen ? 'md:mr-[320px]' : ''}`}>
                
                {/* Hub Header */}
                <header className="p-6 md:p-8 flex justify-between items-center">
                    <div>
                        <h1 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-500 font-orbitron">Showdown Hub</h1>
                        <p className="text-gray-400 mt-1">Welcome to the arena, <span className="text-white font-bold">{user?.displayName}</span>.</p>
                    </div>
                    <button 
                        onClick={() => setIsChatOpen(!isChatOpen)}
                        className="md:hidden p-2 bg-gray-800 rounded-full text-white"
                    >
                        <MessageCircle size={24} />
                    </button>
                </header>

                {/* Hub Content Grid */}
                <div className="flex-grow overflow-y-auto custom-scrollbar p-6 md:p-8 pt-0">
                    {/* Stats Row */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                        {stats.map((stat, index) => (
                            <div key={index} className="bg-gray-900/60 backdrop-blur-md border border-white/10 p-6 rounded-2xl flex items-center gap-4 shadow-lg">
                                <div className={`p-3 rounded-xl bg-white/5 ${stat.color}`}>
                                    <stat.icon size={32} />
                                </div>
                                <div>
                                    <p className="text-gray-400 text-xs uppercase font-bold tracking-wider">{stat.label}</p>
                                    <p className="text-2xl font-bold text-white font-orbitron">{stat.value}</p>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Main Action Buttons */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                        <button onClick={() => setActiveModal('lobby')} className="group relative h-48 rounded-3xl overflow-hidden shadow-2xl transition-all hover:scale-[1.02]">
                            <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-cyan-600 opacity-90 group-hover:opacity-100 transition-opacity" />
                            <div className="absolute inset-0 p-6 flex flex-col justify-between">
                                <Swords size={48} className="text-white/20 mb-auto" />
                                <div>
                                    <h3 className="text-2xl font-bold text-white font-orbitron">Find Match</h3>
                                    <p className="text-blue-100 text-sm">Enter the lobby and challenge rivals.</p>
                                </div>
                            </div>
                        </button>

                        <button onClick={() => setActiveModal('tournaments')} className="group relative h-48 rounded-3xl overflow-hidden shadow-2xl transition-all hover:scale-[1.02]">
                            <div className="absolute inset-0 bg-gradient-to-br from-purple-600 to-pink-600 opacity-90 group-hover:opacity-100 transition-opacity" />
                            <div className="absolute inset-0 p-6 flex flex-col justify-between">
                                <Trophy size={48} className="text-white/20 mb-auto" />
                                <div>
                                    <h3 className="text-2xl font-bold text-white font-orbitron">Tournaments</h3>
                                    <p className="text-purple-100 text-sm">Create or join competitive brackets.</p>
                                </div>
                            </div>
                        </button>

                        <button onClick={() => setActiveModal('spectator')} className="group relative h-48 rounded-3xl overflow-hidden shadow-2xl transition-all hover:scale-[1.02]">
                            <div className="absolute inset-0 bg-gradient-to-br from-amber-600 to-orange-600 opacity-90 group-hover:opacity-100 transition-opacity" />
                            <div className="absolute inset-0 p-6 flex flex-col justify-between">
                                <Target size={48} className="text-white/20 mb-auto" />
                                <div>
                                    <h3 className="text-2xl font-bold text-white font-orbitron">Spectate</h3>
                                    <p className="text-amber-100 text-sm">Watch live high-stakes duels.</p>
                                </div>
                            </div>
                        </button>
                    </div>

                    {/* Secondary Actions */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <button onClick={() => setActiveModal('leaderboard')} className="bg-gray-800/40 hover:bg-gray-800/60 border border-white/5 p-4 rounded-xl text-left transition-colors">
                            <BarChart2 size={24} className="text-cyan-400 mb-2" />
                            <span className="font-bold text-white">Leaderboard</span>
                        </button>
                        <button onClick={() => setActiveModal('quests')} className="bg-gray-800/40 hover:bg-gray-800/60 border border-white/5 p-4 rounded-xl text-left transition-colors">
                            <Target size={24} className="text-green-400 mb-2" />
                            <span className="font-bold text-white">Daily Quests</span>
                        </button>
                        <button onClick={() => setActiveModal('rewards')} className="bg-gray-800/40 hover:bg-gray-800/60 border border-white/5 p-4 rounded-xl text-left transition-colors">
                            <Award size={24} className="text-yellow-400 mb-2" />
                            <span className="font-bold text-white">Rewards</span>
                        </button>
                        <button onClick={() => setActiveModal('matchHistory')} className="bg-gray-800/40 hover:bg-gray-800/60 border border-white/5 p-4 rounded-xl text-left transition-colors">
                            <Swords size={24} className="text-purple-400 mb-2" />
                            <span className="font-bold text-white">History</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Persistent Chat Sidebar (Desktop) */}
            <div className={`
                fixed md:absolute inset-y-0 right-0 w-full md:w-[320px] bg-gray-900/95 backdrop-blur-xl border-l border-white/10 shadow-2xl transform transition-transform duration-300 z-40
                ${isChatOpen ? 'translate-x-0' : 'translate-x-full'}
            `}>
                <div className="h-full flex flex-col">
                    <div className="p-4 border-b border-white/10 flex justify-between items-center bg-black/20">
                        <h2 className="font-bold text-white font-orbitron flex items-center gap-2">
                            <MessageCircle size={18} className="text-cyan-400" /> 
                            Nexus Chat
                        </h2>
                        <button onClick={() => setIsChatOpen(false)} className="md:hidden text-gray-400 hover:text-white">
                            <X size={20} />
                        </button>
                    </div>
                    <div className="flex-grow overflow-hidden">
                        <ShowdownChat nexusId={primaryNexus} />
                    </div>
                </div>
            </div>

            {/* Desktop Chat Toggle (Floating) */}
            {!isChatOpen && (
                <button 
                    onClick={() => setIsChatOpen(true)}
                    className="hidden md:flex fixed bottom-8 right-8 bg-gradient-to-r from-cyan-600 to-blue-600 text-white p-4 rounded-full shadow-lg hover:scale-110 transition-transform z-30"
                >
                    <MessageCircle size={28} />
                </button>
            )}

            {/* Render Active Modal */}
            <AnimatePresence>
                {activeModal && renderModal()}
            </AnimatePresence>
        </div>
    );
};

export default ShowdownPage;